"use client";

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  Bars3Icon,
  BookOpenIcon,
  BuildingLibraryIcon,
  ChevronDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { debounce } from '@/app/lib/utils';
import departmentsData from '@/data/departments.json';
import { sirsApi } from '@/app/services/api';
import type { SIRSCourseStats, SIRSDepartmentStats, SIRSProfessorStats } from '@/app/lib/sirsData';

export interface SIRSSearchResult {
  professors?: SIRSProfessorStats[];
  courses?: SIRSCourseStats[];
  departments?: SIRSDepartmentStats[];
}

interface SIRSSearchProps {
  onProfessorSelect: (name: string) => void;
  onCourseSelect: (courseId: string) => void;
  onDepartmentSelect?: (deptCode: string) => void;
  initialQuery?: string;
  initialSearchType?: SIRSSearchType;
}

export type SIRSSearchType = 'both' | 'professor' | 'course' | 'department';
type SearchType = SIRSSearchType;
type ResultLayout = 'grid' | 'list';

export interface SIRSSearchDirectoryProps extends SIRSSearchProps {
  query: string;
  searchType: SIRSSearchType;
  results: SIRSSearchResult | null;
  loading: boolean;
  error: string | null;
  featured: { departments?: SIRSDepartmentStats[]; professors?: SIRSProfessorStats[] } | null;
  loadingFeatured: boolean;
  onQueryChange: (query: string) => void;
  onSearchTypeChange: (type: SIRSSearchType) => void;
}

const DEPARTMENT_ACCENTS = [
  { border: 'border-l-red-500', dot: 'bg-red-500', avatar: 'from-red-500 to-rose-700' },
  { border: 'border-l-orange-500', dot: 'bg-orange-500', avatar: 'from-orange-500 to-amber-700' },
  { border: 'border-l-amber-500', dot: 'bg-amber-500', avatar: 'from-amber-500 to-yellow-700' },
  { border: 'border-l-emerald-500', dot: 'bg-emerald-500', avatar: 'from-emerald-500 to-teal-700' },
  { border: 'border-l-cyan-500', dot: 'bg-cyan-500', avatar: 'from-cyan-500 to-sky-700' },
  { border: 'border-l-blue-500', dot: 'bg-blue-500', avatar: 'from-blue-500 to-indigo-700' },
  { border: 'border-l-violet-500', dot: 'bg-violet-500', avatar: 'from-violet-500 to-purple-700' },
  { border: 'border-l-pink-500', dot: 'bg-pink-500', avatar: 'from-pink-500 to-rose-700' },
] as const;

const FALLBACK_ACCENT = {
  border: 'border-l-slate-400',
  dot: 'bg-slate-400',
  avatar: 'from-slate-500 to-slate-700',
} as const;

function getDepartmentAccent(department?: string | null) {
  if (!department) return FALLBACK_ACCENT;
  const hash = [...department].reduce((sum, character) => sum + character.charCodeAt(0), 0);
  return DEPARTMENT_ACCENTS[hash % DEPARTMENT_ACCENTS.length];
}

function filterResultsByType(results: SIRSSearchResult | null, type: SearchType): SIRSSearchResult | null {
  if (!results || type === 'both') return results;
  return {
    professors: type === 'professor' ? results.professors : [],
    courses: type === 'course' ? results.courses : [],
    departments: type === 'department' ? results.departments : [],
  };
}

function toTitleCase(value: string): string {
  return value.toLowerCase().split(' ').map((word) =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}

function getDepartmentName(department: string): string {
  const normalizedCode = department.padStart(3, '0');
  const name = (departmentsData as Record<string, string>)[department]
    ?? (departmentsData as Record<string, string>)[normalizedCode];
  return name ? toTitleCase(name) : `Department ${department}`;
}

function getInitials(name: string): string {
  const normalized = name.includes(',')
    ? name.split(',').reverse().join(' ')
    : name;
  const parts = normalized.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'RU';
}

function RatingSummary({ score, label = 'Overall quality' }: { score: number | null; label?: string }) {
  if (score === null || score === undefined) {
    return <span className="text-sm font-medium text-[var(--ss-text-muted)]">{label}: Not rated</span>;
  }

  return (
    <div className="text-right">
      <div className="flex items-center justify-end gap-1.5"><StarSolidIcon className="h-4 w-4 text-amber-400" aria-hidden="true" /><span className="font-bold text-[var(--ss-text)]">{score.toFixed(1)}</span><span className="text-xs text-[var(--ss-text-muted)]">/ 5</span></div>
      <span className="mt-0.5 block text-xs text-[var(--ss-text-muted)]">{label}</span>
    </div>
  );
}

function ProfessorAvatar({ professor, accent }: { professor: SIRSProfessorStats; accent: { avatar: string } }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showOfficialImage = Boolean(professor.official_profile_image_url) && !imageFailed;

  return (
    <div className={`relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br ${accent.avatar} text-base font-bold tracking-wide text-white shadow-sm`}>
      {showOfficialImage ? (
        // URLs only reach this client shape after the server-side Rutgers-host and
        // exact-name/subject validator accepts one unambiguous registry entry.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={professor.official_profile_image_url}
          alt={`Official Rutgers profile photo of ${professor.instructor_name}`}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
          referrerPolicy="no-referrer"
        />
      ) : (
        <span aria-hidden="true">{getInitials(professor.instructor_name)}</span>
      )}
    </div>
  );
}

function ProfessorCard({
  professor,
  onSelect,
  layout,
}: {
  professor: SIRSProfessorStats;
  onSelect: (name: string) => void;
  layout: ResultLayout;
}) {
  const primaryDepartment = professor.primary_department;
  const accent = getDepartmentAccent(primaryDepartment);
  const otherDepartmentCount = Math.max((professor.department_codes?.length ?? 0) - 1, 0);

  return (
    <article
      className={`group relative w-full scroll-my-28 overflow-hidden rounded-xl border border-l-4 ${accent.border} border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 motion-reduce:transform-none hover:border-[var(--ss-focus)] hover:shadow-md ${layout === 'list' ? 'sm:p-4' : ''}`}
    >
      <button type="button" onClick={() => onSelect(professor.instructor_name)} aria-label={`View historical SIRS ratings for ${professor.instructor_name}`} className="absolute inset-0 z-10 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)]" />
      <div className={`flex gap-4 ${layout === 'list' ? 'sm:items-center' : 'items-start'}`}>
        <ProfessorAvatar professor={professor} accent={accent} />

        <div className="min-w-0 flex-1">
          <div className={`flex flex-wrap items-start justify-between gap-2`}>
            <div className="min-w-0">
              <h3 className="line-clamp-2 break-words text-lg font-bold leading-tight text-[var(--ss-text)] transition-colors group-hover:text-[var(--ss-brand)]">
                {professor.instructor_name}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {primaryDepartment ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] px-2.5 py-1 text-xs font-semibold text-[var(--ss-text)] ">
                    <span className={`h-2 w-2 rounded-full ${accent.dot}`} aria-hidden="true" />
                    {primaryDepartment} · {getDepartmentName(primaryDepartment)}
                  </span>
                ) : (
                  <span className="rounded-full bg-[var(--ss-surface-subtle)] px-2.5 py-1 text-xs font-medium text-[var(--ss-text-muted)] ">
                    {(professor.department_codes?.length ?? 0) > 1 ? 'Mixed historical subjects' : 'Department not identified'}
                  </span>
                )}
                {primaryDepartment && otherDepartmentCount > 0 && (
                  <span className="text-xs font-medium text-[var(--ss-text-muted)] ">
                    {otherDepartmentCount <= 3
                      ? `+${otherDepartmentCount} historical ${otherDepartmentCount === 1 ? 'subject' : 'subjects'}`
                      : 'Broad historical subject coverage'}
                  </span>
                )}
              </div>
            </div>

            <div className="text-right">
              <RatingSummary score={professor.avg_teaching_score} label="Teaching effectiveness" />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--ss-text-muted)]">
            <span><strong className="text-[var(--ss-text)]">{professor.unique_courses.toLocaleString()}</strong> <span>courses surveyed</span></span>
            <span><strong className="text-[var(--ss-text)]">{professor.total_enrollment.toLocaleString()}</strong> <span>student enrollments</span></span>
            <span><strong className="text-[var(--ss-text)]">{professor.total_responses.toLocaleString()}</strong> <span>survey responses</span></span>
            <span aria-label="historical span">{professor.first_year === professor.last_year ? professor.first_year : `${professor.first_year}–${professor.last_year}`}</span>
          </div>
        </div>
      </div>
      <span className="mt-2 inline-flex min-h-12 items-center gap-2 rounded-control px-2 text-sm font-semibold text-[var(--ss-focus)]">View historical ratings <ArrowRightIcon className="h-4 w-4" aria-hidden="true" /></span>
    </article>
  );
}

function DepartmentCard({ department, onSelect }: { department: SIRSDepartmentStats; onSelect: (code: string) => void }) {
  const accent = getDepartmentAccent(department.department_code);
  return (
    <button
      type="button"
      onClick={() => onSelect(department.department_code)}
      className={`group w-full rounded-xl border border-l-4 ${accent.border} border-[var(--ss-border)] bg-[var(--ss-surface)] p-5 text-left shadow-sm transition hover:-translate-y-0.5 motion-reduce:transform-none hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--ss-text-muted)] ">Department {department.department_code}</p>
          <h3 className="mt-1 text-lg font-bold text-[var(--ss-text)] group-hover:text-[var(--ss-brand)]">{getDepartmentName(department.department_code)}</h3>
        </div>
        <RatingSummary score={department.avg_quality_score} />
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-[var(--ss-text-muted)] ">
        <span><strong className="text-[var(--ss-text)] ">{department.unique_courses}</strong> courses</span>
        <span><strong className="text-[var(--ss-text)] ">{department.unique_instructors}</strong> professors</span>
        <span><strong className="text-[var(--ss-text)] ">{department.total_enrollment.toLocaleString()}</strong> enrollments</span>
      </div>
    </button>
  );
}

function CourseCard({ course, onSelect }: { course: SIRSCourseStats; onSelect: (id: string) => void }) {
  const accent = getDepartmentAccent(course.course_subject);
  return (
    <button
      type="button"
      onClick={() => onSelect(course.course_id)}
      className={`group w-full rounded-xl border border-l-4 ${accent.border} border-[var(--ss-border)] bg-[var(--ss-surface)] p-5 text-left shadow-sm transition hover:-translate-y-0.5 motion-reduce:transform-none hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-xs font-bold tracking-wide text-[var(--ss-brand)]">{course.course_subject}:{course.course_code}</p>
          <h3 className="mt-1 line-clamp-2 text-lg font-bold text-[var(--ss-text)] group-hover:text-[var(--ss-brand)]">
            {course.course_name || 'Course title unavailable'}
          </h3>
        </div>
        <RatingSummary score={course.avg_quality_score} />
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-[var(--ss-text-muted)] ">
        <span><strong className="text-[var(--ss-text)] ">{course.total_offerings}</strong> surveys</span>
        <span><strong className="text-[var(--ss-text)] ">{course.unique_instructors}</strong> professors</span>
        <span><strong className="text-[var(--ss-text)] ">{course.first_offered}–{course.last_offered}</strong> historical span</span>
      </div>
    </button>
  );
}

function SourceFooter({ years }: { years: number[] }) {
  const firstYear = years.length > 0 ? Math.min(...years) : null;
  const lastYear = years.length > 0 ? Math.max(...years) : null;

  return (
    <footer className="rounded-xl border border-[var(--ss-border)] bg-[var(--ss-surface)] px-4 py-3 text-xs text-[var(--ss-text-muted)]">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold text-[var(--ss-text)] ">Historical SIRS snapshot</span>
        {firstYear && lastYear && <><span aria-hidden="true">·</span><span>{firstYear}–{lastYear} in these results</span></>}
        <span aria-hidden="true">·</span>
        <span>Cached for faster browsing</span>
      </div>
      <details className="group mt-2">
        <summary className="inline-flex min-h-12 min-w-12 cursor-pointer list-none items-center gap-1 font-semibold text-[var(--ss-text)] hover:text-[var(--ss-brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]  ">
          Data notes
          <ChevronDownIcon className="h-3.5 w-3.5 transition-transform group-open:rotate-180" aria-hidden="true" />
        </summary>
        <p className="mt-2 max-w-3xl leading-relaxed">
          Ratings aggregate Rutgers Student Instructional Rating Survey records collected in prior terms. Course and department markers describe those historical records; they do not indicate that a professor currently teaches a course, belongs to a department now, or is available in the current catalog. Cached results can lag source updates.
        </p>
      </details>
    </footer>
  );
}

export function SIRSSearchDirectory({
  query,
  searchType,
  results,
  loading,
  error,
  featured,
  loadingFeatured,
  onQueryChange,
  onSearchTypeChange,
  onProfessorSelect,
  onCourseSelect,
  onDepartmentSelect,
}: SIRSSearchDirectoryProps) {
  const [layout, setLayout] = useState<ResultLayout>('grid');
  const scopedResults = filterResultsByType(results, searchType);
  const visibleResults = scopedResults ? { ...scopedResults, departments: onDepartmentSelect ? scopedResults.departments : [] } : null;
  const displayedFeatured = featured ? {
    professors: searchType === 'both' || searchType === 'professor' ? featured.professors : [],
    departments: onDepartmentSelect && (searchType === 'both' || searchType === 'department') ? featured.departments : [],
  } : null;
  const activeData = visibleResults ?? displayedFeatured;
  const years = [
    ...(activeData?.professors ?? []).flatMap((professor) => [professor.first_year, professor.last_year]),
    ...(activeData?.departments ?? []).flatMap((department) => [department.first_year, department.last_year]),
    ...(visibleResults?.courses ?? []).flatMap((course) => [course.first_offered, course.last_offered]),
  ].filter((year): year is number => typeof year === 'number' && year > 0);
  const resultCount = (visibleResults?.professors?.length ?? 0) + (visibleResults?.courses?.length ?? 0) + (visibleResults?.departments?.length ?? 0);
  const professorGridClass = layout === 'grid' ? 'grid grid-cols-1 gap-4 lg:grid-cols-2' : 'grid grid-cols-1 gap-4';
  const typeOptions: Array<{ value: SearchType; label: string }> = [
    { value: 'both', label: 'All' },
    { value: 'professor', label: 'Professors' },
    { value: 'course', label: 'Courses' },
    ...(onDepartmentSelect ? [{ value: 'department' as const, label: 'Departments' }] : []),
  ];

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-[var(--ss-border)] bg-[var(--ss-surface)] shadow-sm" aria-label="SIRS search controls">
        <div className="space-y-4 p-5 sm:p-6">
          <label className="block" htmlFor="sirs-search-input">
            <span className="mb-2 block text-sm font-semibold text-[var(--ss-text)]">Search SIRS records</span>
            <span className="relative block"><MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--ss-text-muted)]" aria-hidden="true" /><input id="sirs-search-input" type="search" value={query} onChange={(event) => onQueryChange(event.target.value)} placeholder="Professor name, course title, or 198:111" className="min-h-12 w-full rounded-xl border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] py-3 pl-11 pr-4 text-[var(--ss-text)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]" /></span>
          </label>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2 pb-1" role="group" aria-label="Filter result type">
              <FunnelIcon className="h-4 w-4 shrink-0 text-[var(--ss-text-muted)]" aria-hidden="true" />
              {typeOptions.map((option) => <button key={option.value} type="button" onClick={() => onSearchTypeChange(option.value)} aria-pressed={searchType === option.value} className={`min-h-12 min-w-12 shrink-0 rounded-lg px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${searchType === option.value ? 'bg-[var(--ss-brand)] text-[var(--ss-brand-contrast)]' : 'bg-[var(--ss-surface-subtle)] text-[var(--ss-text)]'}`}>{option.label}</button>)}
            </div>
            <div className="hidden rounded-lg border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-1 lg:flex" role="group" aria-label="Result layout"><button type="button" onClick={() => setLayout('grid')} aria-pressed={layout === 'grid'} aria-label="Grid view" className={`min-h-12 min-w-12 rounded-md p-2 focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${layout === 'grid' ? 'bg-[var(--ss-surface)] text-[var(--ss-brand)] shadow-sm' : 'text-[var(--ss-text-muted)]'}`}><Squares2X2Icon className="h-4 w-4" aria-hidden="true" /></button><button type="button" onClick={() => setLayout('list')} aria-pressed={layout === 'list'} aria-label="List view" className={`min-h-12 min-w-12 rounded-md p-2 focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${layout === 'list' ? 'bg-[var(--ss-surface)] text-[var(--ss-brand)] shadow-sm' : 'text-[var(--ss-text-muted)]'}`}><Bars3Icon className="h-4 w-4" aria-hidden="true" /></button></div>
          </div>
        </div>
      </section>

      {loading ? <div className="py-12 text-center" role="status"><p className="text-sm text-[var(--ss-text-muted)]">Searching historical SIRS records…</p></div> : null}
      {error ? <div role="alert" className="rounded-xl border border-[var(--ss-critical)] bg-[var(--ss-critical-bg)] p-4 text-sm text-[var(--ss-critical)]">{error}</div> : null}
      {!loading && !error && visibleResults ? <div className="space-y-8" aria-live="polite" aria-label="SIRS search results"><div className="flex items-baseline justify-between gap-4"><h2 className="text-xl font-bold text-[var(--ss-text)]">Search results</h2><span className="text-sm text-[var(--ss-text-muted)]">{resultCount} {resultCount === 1 ? 'match' : 'matches'}</span></div>{visibleResults.professors?.length ? <section aria-label="Professor results"><h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[var(--ss-text-muted)]">Professors · {visibleResults.professors.length}</h3><div className={professorGridClass}>{visibleResults.professors.map((professor) => <ProfessorCard key={professor.instructor_name} professor={professor} onSelect={onProfessorSelect} layout={layout} />)}</div></section> : null}{visibleResults.courses?.length ? <section aria-label="Course results"><h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[var(--ss-text-muted)]">Courses · {visibleResults.courses.length}</h3><div className={`grid gap-4 ${layout === 'grid' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>{visibleResults.courses.map((course) => <CourseCard key={course.course_id} course={course} onSelect={onCourseSelect} />)}</div></section> : null}{visibleResults.departments?.length && onDepartmentSelect ? <section aria-label="Department results"><h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[var(--ss-text-muted)]">Departments · {visibleResults.departments.length}</h3><div className={`grid gap-4 ${layout === 'grid' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>{visibleResults.departments.map((department) => <DepartmentCard key={department.department_code} department={department} onSelect={onDepartmentSelect} />)}</div></section> : null}{resultCount === 0 ? <div className="rounded-2xl border border-dashed border-[var(--ss-border)] px-6 py-12 text-center"><h3 className="font-semibold text-[var(--ss-text)]">No historical records found</h3><p className="mt-1 text-sm text-[var(--ss-text-muted)]">Try a surname, subject code, or shorter course title.</p></div> : null}</div> : null}
      {!loading && !error && !results && query.trim().length < 2 && featured ? <div className="space-y-8">{displayedFeatured?.professors?.length ? <section aria-labelledby="sirs-featured-professors"><h2 id="sirs-featured-professors" className="mb-3 text-xl font-bold text-[var(--ss-text)]">Frequently surveyed professors</h2><div className={professorGridClass}>{displayedFeatured.professors.map((professor) => <ProfessorCard key={professor.instructor_name} professor={professor} onSelect={onProfessorSelect} layout={layout} />)}</div></section> : null}{displayedFeatured?.departments?.length && onDepartmentSelect ? <section aria-labelledby="sirs-featured-departments"><h2 id="sirs-featured-departments" className="mb-3 text-xl font-bold text-[var(--ss-text)]">Browse departments</h2><div className={`grid gap-4 ${layout === 'grid' ? 'lg:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>{displayedFeatured.departments.map((department) => <DepartmentCard key={department.department_code} department={department} onSelect={onDepartmentSelect} />)}</div></section> : null}{searchType === 'course' ? <section className="rounded-2xl border border-dashed border-[var(--ss-border)] px-6 py-12 text-center" aria-label="Course search prompt"><h2 className="text-lg font-bold text-[var(--ss-text)]">Search historical course ratings</h2><p className="mt-1 text-sm text-[var(--ss-text-muted)]">Enter a course title, subject code, or course number above.</p></section> : null}</div> : null}
      {loadingFeatured && query.trim().length < 2 ? <div className="py-12 text-center" role="status">Loading SIRS browse data…</div> : null}
      {!loading && !loadingFeatured && !error && activeData ? <SourceFooter years={years} /> : null}
      {!loading && !loadingFeatured && !error && !results && !featured ? <div className="rounded-2xl border border-dashed border-[var(--ss-border)] px-6 py-12 text-center"><h2 className="text-lg font-bold text-[var(--ss-text)]">Search the historical ratings catalog</h2><p className="mt-1 text-sm text-[var(--ss-text-muted)]">Enter at least two characters to begin.</p></div> : null}
    </div>
  );
}

export default function SIRSSearch({
  onProfessorSelect,
  onCourseSelect,
  onDepartmentSelect,
  initialQuery = '',
  initialSearchType = 'both',
}: SIRSSearchProps) {
  const [query, setQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState<SearchType>(initialSearchType);
  const [results, setResults] = useState<SIRSSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [featured, setFeatured] = useState<{ departments?: SIRSDepartmentStats[]; professors?: SIRSProfessorStats[] } | null>(null);
  const [loadingFeatured, setLoadingFeatured] = useState(false);

  useEffect(() => setQuery(initialQuery), [initialQuery]);
  useEffect(() => setSearchType(initialSearchType), [initialSearchType]);

  const performSearch = useCallback(async (searchQuery: string, type: SearchType) => {
    if (searchQuery.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await sirsApi.search(searchQuery.trim(), type);
      setResults(filterResultsByType(data.results, type));
    } catch {
      setError('Search is temporarily unavailable. Please try again.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((value: string, type: SearchType) => performSearch(value, type), 300),
    [performSearch]
  );

  useEffect(() => {
    if (query.trim().length >= 2) debouncedSearch(query, searchType);
    else setResults(null);
  }, [query, searchType, debouncedSearch]);

  useEffect(() => {
    if (query.trim().length >= 2) {
      setFeatured(null);
      return;
    }
    const controller = new AbortController();
    setLoadingFeatured(true);
    sirsApi.getFeatured(controller.signal)
      .then(setFeatured)
      .catch((caughtError) => {
        if (caughtError.name !== 'AbortError') console.error('Failed to load featured SIRS data:', caughtError);
      })
      .finally(() => setLoadingFeatured(false));
    return () => controller.abort();
  }, [query]);

  return <SIRSSearchDirectory
    query={query} searchType={searchType} results={results} loading={loading} error={error}
    featured={featured} loadingFeatured={loadingFeatured}
    onQueryChange={setQuery} onSearchTypeChange={setSearchType}
    onProfessorSelect={onProfessorSelect} onCourseSelect={onCourseSelect}
    onDepartmentSelect={onDepartmentSelect}
  />;
}
